// Bill period math and the rolling coverage window. See ADR-0011.
//
// Conventions every helper here shares (and every bill surface should reuse):
//
// - Dates become whole UTC day numbers. The API emits both ISO YYYY-MM-DD
//   strings and RFC-1123 GMT strings ("Thu, 02 Jul 2026 00:00:00 GMT"); both
//   name a calendar day with no local meaning, so UTC keeps the answer the same
//   for viewers in every timezone. Local getMonth() on an RFC-1123 date moves a
//   bill ending on the 1st into the previous month for anyone west of UTC.
// - Service periods are half-open [period_start, period_end). Providers
//   commonly end one period and start the next on the same meter-read date
//   (Jun 2 – Jul 2, then Jul 2 – Aug 4), and that shared day is not an overlap.
// - A bill's amount belongs to the month of its period_end (the backend's
//   trailing-average convention). Whether a month is *covered* is a separate
//   question, answered from the actual service periods: billing-cycle drift can
//   end two bills in one month and none in the next without any day missing.

import { ISO_DATE_RE, isIsoDate } from './billFormat';

export const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const DAY_MS = 86_400_000;
/**
 * Seams up to this many days between consecutive periods count as continuous.
 * Inclusive-end providers (Jun 1 – Jun 30, then Jul 1 – Jul 31) leave a one-day
 * half-open seam that is not a real gap.
 */
const GAP_TOLERANCE_DAYS = 3;
const WINDOW_MONTHS = 12;
const LOOKAHEAD_MONTHS = 3;

/**
 * Parse an API date to a UTC day number, or null. ISO strings are validated
 * first: `new Date('2026-02-30')` silently rolls into March, which would file
 * the bill under the wrong month.
 */
export const toUtcDay = (value) => {
  if (!value || typeof value !== 'string') return null;
  if (ISO_DATE_RE.test(value)) {
    if (!isIsoDate(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    return Date.UTC(year, month - 1, day) / DAY_MS;
  }
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / DAY_MS);
};

/** ISO YYYY-MM-DD for a UTC day number, suitable for billFormat's formatDate. */
export const utcDayToIso = (day) => new Date(day * DAY_MS).toISOString().slice(0, 10);

/** Months since year 0 (year * 12 + month0) for a UTC day number. */
export const monthIndexOfDay = (day) => {
  const date = new Date(day * DAY_MS);
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
};

const firstDayOfMonthIndex = (monthIndex) =>
  Date.UTC(Math.floor(monthIndex / 12), monthIndex % 12, 1) / DAY_MS;

const toAmount = (value) => {
  if (value == null || value === '') return null;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(num) ? num : null;
};

/** Days two half-open periods share; 0 when they only touch at a read date. */
export const periodOverlapDays = (a, b) =>
  Math.max(0, Math.min(a.end, b.end) - Math.max(a.start, b.start));

/**
 * Normalize a bill into a period, or null when either boundary is missing,
 * unparseable, or reversed (the backend rejects those, so they are stale data).
 */
export const toPeriod = (bill) => {
  const start = toUtcDay(bill?.period_start);
  const end = toUtcDay(bill?.period_end);
  if (start == null || end == null || end < start) return null;
  return { bill, start, end, monthIndex: monthIndexOfDay(end), amount: toAmount(bill.amount) };
};

/**
 * Pairs of periods that share at least one day. Identical periods are skipped:
 * uq_utility_bill_utility_period makes them impossible server-side, so a match
 * can only be a stale client list.
 */
export const findOverlappingPeriods = (periods) => {
  const pairs = [];
  for (let i = 0; i < periods.length; i += 1) {
    for (let j = i + 1; j < periods.length; j += 1) {
      const a = periods[i];
      const b = periods[j];
      if (a.start === b.start && a.end === b.end) continue;
      const days = periodOverlapDays(a, b);
      if (days > 0) pairs.push({ a, b, days });
    }
  }
  return pairs;
};

const descending = (x, y) => (x === y ? 0 : x < y ? 1 : -1);
const dayOrFloor = (value) => toUtcDay(value) ?? -Infinity;

/**
 * Newest period_end first (ties: newest period_start), matching the list
 * endpoint's ORDER BY. Every client-side merge into the bill list uses this so
 * a freshly saved bill lands where a reload would put it.
 */
export const sortBillsNewestFirst = (bills) =>
  [...bills].sort(
    (a, b) =>
      descending(dayOrFloor(a.period_end), dayOrFloor(b.period_end)) ||
      descending(dayOrFloor(a.period_start), dayOrFloor(b.period_start)),
  );

/** Merge periods across seams <= GAP_TOLERANCE_DAYS; report the wider gaps. */
const mergePeriods = (periods) => {
  const sorted = [...periods].sort((a, b) => a.start - b.start);
  const merged = [];
  const gaps = [];
  for (const { start, end } of sorted) {
    const last = merged[merged.length - 1];
    if (last && start - last.end <= GAP_TOLERANCE_DAYS) {
      last.end = Math.max(last.end, end);
      continue;
    }
    if (last) gaps.push({ startDay: last.end, endDay: start, days: start - last.end });
    merged.push({ start, end });
  }
  return { merged, gaps };
};

const sumAmounts = (periods) => {
  const amounts = periods.map((p) => p.amount).filter((a) => a != null);
  return amounts.length ? amounts.reduce((total, a) => total + a, 0) : null;
};

/** "Sep 2026" (style 'short') or "September 2026" (style 'long'), in UTC. */
export const monthName = (monthIndex, style) =>
  new Date(Date.UTC(Math.floor(monthIndex / 12), monthIndex % 12, 1)).toLocaleString('en-US', {
    month: style,
    year: 'numeric',
    timeZone: 'UTC',
  });

/**
 * Build the rolling coverage strip: the 12 months ending with the current
 * month, plus the next 3, optionally paged back a year at a time.
 *
 * Tile status, in priority order:
 *   billed      - at least one bill's period_end falls in the month (amount = sum)
 *   covered     - no bill ends here, but bill periods cover every day of the
 *                 month from the start of recorded history (cycle drift)
 *   no_history  - past month that ends before the first bill starts
 *   missing     - any other past month
 *   in_progress - the current month, not yet billed
 *   upcoming    - later months
 *
 * "Current month" is the viewer's local calendar month; bill months are UTC.
 *
 * @param {Array} bills - bills as returned by the API, in any order.
 * @param {{today?: Date, pagesBack?: number}} [options]
 */
export const buildCoverageWindow = (bills = [], { today = new Date(), pagesBack = 0 } = {}) => {
  // Sorted so tile bill lists and overlap pairs come out the same whatever order
  // the caller's list is in (a freshly saved bill is merged client-side).
  const periods = bills
    .map(toPeriod)
    .filter(Boolean)
    .sort((a, b) => descending(a.end, b.end) || descending(a.start, b.start));
  const currentMonthIndex = today.getFullYear() * 12 + today.getMonth();

  const earliestEndMonth = periods.length ? Math.min(...periods.map((p) => p.monthIndex)) : null;
  const maxPagesBack =
    earliestEndMonth == null
      ? 0
      : Math.max(
          0,
          Math.ceil((currentMonthIndex - (WINDOW_MONTHS - 1) - earliestEndMonth) / WINDOW_MONTHS),
        );
  const page = Math.min(Math.max(0, Math.floor(pagesBack) || 0), maxPagesBack);
  const endMonthIndex = currentMonthIndex - WINDOW_MONTHS * page;
  const firstMonthIndex = endMonthIndex - (WINDOW_MONTHS - 1);
  const lastMonthIndex = endMonthIndex + LOOKAHEAD_MONTHS;

  const byMonth = new Map();
  for (const period of periods) {
    if (!byMonth.has(period.monthIndex)) byMonth.set(period.monthIndex, []);
    byMonth.get(period.monthIndex).push(period);
  }

  const { merged, gaps } = mergePeriods(periods);
  const historyStart = periods.length ? Math.min(...periods.map((p) => p.start)) : null;
  const historyEnd = periods.length ? Math.max(...periods.map((p) => p.end)) : null;
  const isCovered = (from, to) => merged.some((m) => m.start <= from && m.end >= to);

  const overlaps = findOverlappingPeriods(periods);
  const overlapping = new Set(overlaps.flatMap(({ a, b }) => [a, b]));

  const months = [];
  for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex += 1) {
    const startDay = firstDayOfMonthIndex(monthIndex);
    const endDay = firstDayOfMonthIndex(monthIndex + 1);
    const own = byMonth.get(monthIndex) || [];
    const isLookahead = monthIndex > endMonthIndex;
    const month = monthIndex % 12;

    let status;
    let coveredBy = [];
    if (own.length) {
      status = 'billed';
    } else if (
      historyStart != null &&
      endDay > historyStart &&
      isCovered(Math.max(startDay, historyStart), endDay)
    ) {
      status = 'covered';
      coveredBy = periods.filter((p) => p.start < endDay && p.end > startDay).map((p) => p.bill);
    } else if (monthIndex < currentMonthIndex) {
      status = historyStart == null || endDay <= historyStart ? 'no_history' : 'missing';
    } else if (monthIndex === currentMonthIndex) {
      status = 'in_progress';
    } else {
      status = 'upcoming';
    }

    const showYear =
      monthIndex === firstMonthIndex || month === 0 || monthIndex === endMonthIndex + 1;
    months.push({
      monthIndex,
      year: Math.floor(monthIndex / 12),
      label: MONTH_LABELS[month],
      longLabel: monthName(monthIndex, 'long'),
      yearMarker: showYear ? `’${String(Math.floor(monthIndex / 12)).slice(-2)}` : null,
      isLookahead,
      isCurrent: monthIndex === currentMonthIndex,
      startDay,
      endDay,
      status,
      amount: sumAmounts(own),
      lastYearAmount: sumAmounts(byMonth.get(monthIndex - 12) || []),
      bills: own.map((p) => p.bill),
      coveredBy,
      hasOverlap: own.some((p) => overlapping.has(p)),
    });
  }

  const visibleStart = months[0].startDay;
  const visibleEnd = months[months.length - 1].endDay;
  const inView = (monthIndex) => monthIndex >= firstMonthIndex && monthIndex <= lastMonthIndex;

  return {
    label: `${monthName(firstMonthIndex, 'short')} – ${monthName(endMonthIndex, 'short')}`,
    coveredCount: months
      .filter((m) => !m.isLookahead)
      .filter((m) => m.status === 'billed' || m.status === 'covered').length,
    pagesBack: page,
    maxPagesBack,
    canGoBack: page < maxPagesBack,
    canGoForward: page > 0,
    months,
    issues: {
      overlaps: overlaps
        .filter(({ a, b }) => inView(a.monthIndex) || inView(b.monthIndex))
        .map(({ a, b, days }) => ({ a: a.bill, b: b.bill, days })),
      gaps: gaps.filter((g) => g.startDay < visibleEnd && g.endDay > visibleStart),
      noBillsAfterDay: months.some((m) => m.status === 'missing' && m.startDay >= historyEnd)
        ? historyEnd
        : null,
    },
  };
};
